import {
  useApiQuery,
  useApiMutation,
  useFileUpload,
} from '@tasama/api-client-query';
import type { KybCompanyDetailsData } from '@tasama/types';
import type {
  UserProfile,
  DocumentsResponse,
  KeyAccountManager,
  WorkflowStatuses,
} from '../types';
import { useUserContext } from '../../../stores/user-context.store';

/**
 * Custom hooks for My Account API operations
 * Uses React Query for data fetching with built-in data transformation
 *
 * NOTE: All hooks use React Query's caching, so multiple components
 * calling the same hook will share the same cached data and only make
 * one API call
 */

// ============================================================================
// QUERY HOOKS WITH DATA TRANSFORMATION
// ============================================================================

/**
 * Base hook for fetching my-account details
 * This is the SINGLE SOURCE OF TRUTH for /my-account/my-details endpoint
 * All other hooks (useUserProfile, useKeyAccountManager, etc.) use this internally
 */
const useMyAccountDetails = () => {
  return useApiQuery<any>(
    ['my-account', 'my-details'], // Single shared query key
    '/my-account/my-details',
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    } as any
  );
};

/**
 * Hook for fetching user profile with automatic data transformation
 * Uses the shared useMyAccountDetails hook to prevent duplicate API calls
 */
export const useUserProfile = () => {
  const query = useMyAccountDetails();
  const userContext = useUserContext();

  // TDF-1393: the backend now populates primaryContact from the acting Super
  // Admin's JWT when no contact row exists, and flags that response with
  // isSuperAdminIdentity=true. Read the flag directly: deriving it from an
  // absent primaryContact would misfire now that a contact is always present.
  // Fall back to the Keycloak identity from userContext for a Super Admin who
  // hit an older backend that has not populated the response yet.
  const isSuperAdminIdentity =
    query.data?.isSuperAdminIdentity === true ||
    (userContext?.isSuperAdmin === true && !query.data?.primaryContact);

  if (isSuperAdminIdentity && !query.data?.primaryContact) {
    const [first = '', ...rest] = (userContext?.name ?? '').split(' ');
    return {
      ...query,
      // The 404 is the expected shape for a non-member, not a failure to show.
      error: null,
      isSuperAdminIdentity: true as const,
      data: {
        firstName: userContext?.firstName || first,
        lastName: rest.join(' '),
        salutation: '',
        jobTitle: '',
        email: userContext?.email || '',
        mobilePhone: '',
        countryCode: '',
      } as UserProfile,
    };
  }

  return {
    ...query,
    isSuperAdminIdentity: isSuperAdminIdentity as boolean,
    data: query.data
      ? (() => {
          const primaryContact = query.data.primaryContact || query.data;
          return {
            firstName: primaryContact.firstName || '',
            lastName: primaryContact.lastName || '',
            salutation: primaryContact.salutation || '',
            jobTitle: primaryContact.jobTitle || '',
            email: primaryContact.email || '',
            mobilePhone: primaryContact.mobile || '',
            countryCode:
              primaryContact.countryCode || query.data.countryCode || '',
          } as UserProfile;
        })()
      : undefined,
  };
};

/**
 * Hook for fetching key account manager data with automatic data transformation
 * Uses the shared useMyAccountDetails hook to prevent duplicate API calls
 */
export const useKeyAccountManager = () => {
  const query = useMyAccountDetails();
  const userContext = useUserContext();

  // TDF-1393: this shares the my-details call. On an older backend the call
  // fails for a Super Admin acting for a client (no contact row exists); the
  // guard suppresses the error so MyAccountPage does not render its error
  // screen over the Keycloak identity fallback. On a newer backend the
  // response is populated (including keyAccountManager, which is the client's
  // real KAM and should still render), and the isSuperAdminIdentity flag is
  // set, so the guard only nulls out data when the response is genuinely
  // absent.
  const isSuperAdminNoData =
    userContext?.isSuperAdmin === true && !query.data?.primaryContact;
  if (isSuperAdminNoData) {
    return { ...query, error: null, data: undefined };
  }

  return {
    ...query,
    data: query.data
      ? (() => {
          // Strip Unicode replacement characters (U+FFFD) from name — backend encoding issue
          const name = (query.data.keyAccountManager?.name || '').replace(
            /\uFFFD/g,
            ''
          );
          const phoneNumber = query.data.keyAccountManager?.phone || '';
          const email = query.data.keyAccountManager?.email || '';

          // Return undefined if all three attributes are blank
          if (!name && !phoneNumber && !email) {
            return undefined;
          }

          return {
            name,
            phoneNumber,
            email,
          } as KeyAccountManager;
        })()
      : undefined,
  };
};

/**
 * Hook for fetching workflow statuses with automatic data transformation
 * Uses the shared useMyAccountDetails hook to prevent duplicate API calls
 */
export const useWorkflowStatuses = () => {
  const query = useCompanyDetails();

  return {
    ...query,
    data: query.data
      ? ({
          onboardingApprovedAt:
            query.data.workflowStatuses.onboardingApprovedAt,
          onboardingStatus: query.data.workflowStatuses.onboardingStatus,
          kybSubmittedAt: query.data.workflowStatuses.kybSubmittedAt,
          kybStatus: query.data.workflowStatuses.kybStatus,
          ndaSubmittedAt: query.data.workflowStatuses.ndaSubmittedAt,
          ndaStatus: query.data.workflowStatuses.ndaStatus,
        } as WorkflowStatuses)
      : undefined,
  };
};

/**
 * Hook for fetching company details with automatic data transformation
 * Flattens address structure for easier form handling
 */
export const useCompanyDetails = () => {
  return useApiQuery<KybCompanyDetailsData>(
    ['my-account', 'company-details'],
    '/my-account/company-details',
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
      select: (data: KybCompanyDetailsData) => ({
        ...data,
        annualRevenue: data.annualRevenue
          ? data.annualRevenue.replace('–', '-')
          : '',
        numberOfEmployees: data.numberOfEmployees
          ? data.numberOfEmployees.replace('–', '-')
          : '',
      }),
    } as any
  );
};

export const useMyAccountDocuments = () => {
  return useApiQuery<DocumentsResponse>(
    ['my-account', 'documents'],
    '/my-account/documents',
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    } as any
  );
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

// Utility function to parse API validation errors
const parseApiValidationErrors = (
  error: any
): Record<string, string> | null => {
  try {
    // Check if it's a validation error response
    if (
      error?.response?.status === 400 &&
      error?.response?.data?.error === 'ValidationError' &&
      error?.response?.data?.validationErrors
    ) {
      const validationErrors = error.response.data.validationErrors;
      const fieldErrors: Record<string, string> = {};

      validationErrors.forEach((validationError: any) => {
        // Use the first constraint as the error message
        if (
          validationError.constraints &&
          validationError.constraints.length > 0
        ) {
          fieldErrors[validationError.field] = validationError.constraints[0];
        }
      });

      return fieldErrors;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Hook for updating user profile
 * Does NOT auto-invalidate queries to prevent infinite refetch loops
 * Components should manually refetch or update cache as needed
 */
export const useUpdateMyAccountDetails = () => {
  return useApiMutation<UserProfile, UserProfile>(
    '/my-account/my-details',
    'PUT',
    {
      // Remove automatic invalidation to prevent continuous GET calls that crash BFF
      // Components will handle cache updates manually after successful mutation
      onSuccess: (_data) => {},
      onError: (error) => {
        // Parse validation errors and attach them to the error object
        const validationErrors = parseApiValidationErrors(error);
        if (validationErrors) {
          (error as any).validationErrors = validationErrors;
        }
      },
    }
  );
};

/**
 * Hook for updating company details
 * Invalidates company-details queries to refresh data
 */
export const useUpdateCompanyDetails = () => {
  return useApiMutation<any, any>('/my-account/company-details', 'PUT', {
    invalidateKeys: ['my-account', 'company-details'],
    onSuccess: (_data) => {},
    onError: (error) => {
      // Parse validation errors and attach them to the error object
      const validationErrors = parseApiValidationErrors(error);
      if (validationErrors) {
        (error as any).validationErrors = validationErrors;
      }
    },
  });
};

/**
 * Hook for downloading documents
 */
export const useDownloadDocument = () => {
  return useApiMutation<Blob, string>(
    (documentId) => `/my-account/documents/${documentId}/download`,
    'GET' as any, // Temporary workaround - will need to extend useApiMutation to support GET
    {
      onSuccess: (_data, _documentId) => {},
      onError: (_error) => {},
    }
  );
};

/**
 * Hook for uploading company documents
 * Used during company profile editing (KYB process)
 * Uses multipart/form-data for file uploads
 */
export const useUploadCompanyDocuments = () => {
  return useFileUpload<any>('/my-account/company-details/upload', {
    invalidateKeys: ['my-account', 'company-details', 'documents'],
    onSuccess: () => {},
    onError: (_error) => {},
  });
};

/**
 * Hook for uploading company logo
 */
export const useUploadLogo = () => {
  return useFileUpload<{ fileId: string }>(
    '/files/logo?entityType=KYB_ATTEMPT'
  );
};

export const useCompanyLogoUrl = (): {
  data: string | null;
  isLoading: boolean;
} => {
  const userContext = useUserContext();
  const fileId = userContext?.logoFileId;
  const baseURL = apiClient.axios.defaults.baseURL ?? '';
  const data = fileId ? `${baseURL}/files/logo/${fileId}` : null;
  return { data, isLoading: false };
};

// Export the utility function for use in other components
export { parseApiValidationErrors };

// ============================================================================
// LEGACY IMPERATIVE API (DEPRECATED - for backward compatibility only)
// ============================================================================
// TODO: Remove this once all stores and components migrate to React Query hooks

import { apiClient } from '@tasama/api-client-query';

/**
 * @deprecated Use React Query hooks instead (useUserProfile, useCompanyDetails, etc.)
 * This object exists only for backward compatibility with legacy stores
 */
export const myAccountApi = {
  /**
   * @deprecated Use useUserProfile hook instead
   */
  getMyDetails: async () => {
    try {
      const response = await apiClient.axios.get('/my-account/my-details');
      return {
        status: 'success' as const,
        data: response.data,
        message: 'Success',
      };
    } catch (error: any) {
      return {
        status: 'error' as const,
        data: null,
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch user details',
      };
    }
  },

  /**
   * @deprecated Use useCompanyDetails hook instead
   */
  getCompanyDetails: async () => {
    try {
      const response = await apiClient.axios.get('/my-account/company-details');
      return {
        status: 'success' as const,
        data: response.data,
        message: 'Success',
      };
    } catch (error: any) {
      return {
        status: 'error' as const,
        data: null,
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch company details',
      };
    }
  },
};
